/**
 * „ANGEBOT IN ÄNDERUNG“ — die Statuskarte oben im Builder (Antoine 31.10.2026, Mockup Vorschlag A freigegeben).
 *
 * Ersetzt den grauen Textblock des Änderungsmodus nach „Für Änderung öffnen“. Zeigt in einem Blick:
 * was weiter gilt, was neu ist, was der nächste Schritt ist. Rechnet NICHTS: alle Beträge kommen
 * unverändert aus `/api/events/:id/kue-differenz` und `/cost-acceptances` (dieselben Quellen wie vorher).
 *
 * Der aktuelle Schritt wird aus dem tatsächlichen Zustand abgeleitet:
 *   - neue Kostenübernahme angefordert (offene Fassung nach der gültigen) -> Schritt 4 „… unterschreibt“
 *   - geändertes Angebot nach dem Öffnen gesendet (Verlauf) -> Schritt 3 „Neue Kostenübernahme anfordern“
 *   - sonst Schritt 2 „Ändern und senden“.
 */
import { useApi } from "../../hooks/useApi";
import { Card, Icon } from "../components";
import { formatDate, money } from "../format";
import { AenderungVerwerfenKnopf, vorzeichen, versionsName, type KueDifferenzDaten, type KueVersion } from "./KostenuebernahmeVersionen";

export type AenderungSchritt = 2 | 3 | 4;

/** Welcher Schritt gerade dran ist — aus Kostenübernahme-Versionen und Verlauf, nichts Neues. */
export function aenderungSchritt(neueKue: KueVersion | null, angebotNachOeffnenGesendet: boolean): AenderungSchritt {
  if (neueKue) return 4;
  if (angebotNachOeffnenGesendet) return 3;
  return 2;
}

const wert = (cents: number | null | undefined) => (cents != null ? money(cents) : "–");

function Kennzahl({ label, betrag, zusatz, testId }: { label: string; betrag: string; zusatz: string; testId: string }) {
  return (
    <div
      data-testid={testId}
      className="min-w-0 border-outline-variant px-4 py-3 [&:nth-child(n+3)]:border-t max-sm:[&:nth-child(even)]:border-l sm:border-l sm:first:border-l-0 sm:[&:nth-child(n+3)]:border-t-0 dark:border-white/10"
    >
      <small className="block font-label-md text-[11px] font-bold uppercase tracking-[0.1em] text-secondary dark:text-secondary-fixed-dim">{label}</small>
      <b className="mt-0.5 block text-[17px] tabular-nums text-on-background dark:text-inverse-on-surface">{betrag}</b>
      <span className="block min-h-[18px] font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">{zusatz}</span>
    </div>
  );
}

export function AenderungStatuskarte({
  eventId,
  gueltige,
  neueKue,
  kundenName,
  angebotGesendetAm,
  onVerworfen,
  versionen,
}: {
  eventId: string;
  gueltige: KueVersion;
  /** Eine nach der gültigen angeforderte, noch offene Fassung (sonst null). */
  neueKue: KueVersion | null;
  kundenName?: string | null;
  /** Wann das geänderte Angebot nach dem Öffnen gesendet wurde (aus dem Verlauf), sonst null. */
  angebotGesendetAm: string | null;
  onVerworfen?: () => void;
  versionen: KueVersion[];
}) {
  const roh = useApi<KueDifferenzDaten | null>(`/api/events/${eventId}/kue-differenz`).data;
  const diff = roh && typeof roh === "object" && "alt" in roh ? roh : null;
  const kunde = kundenName?.trim() || "der Gast";
  const schritt = aenderungSchritt(neueKue, angebotGesendetAm != null);
  const gesendetAm = angebotGesendetAm ?? neueKue?.sentAt ?? null;
  const nummer = diff?.alt.nummer ?? `KÜ ${versionsName(gueltige)}`;
  const gueltigCents = diff?.alt.cents ?? gueltige.amountGrossCents;
  const schritte = ["Für Änderung geöffnet", "Ändern und senden", "Neue Kostenübernahme anfordern", `${kunde} unterschreibt`];

  return (
    <Card as="section" className="mb-3 overflow-hidden !p-0" data-testid="aenderung-offen-banner" aria-label="Angebot in Änderung">
      <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-3 px-5 py-4">
        <div className="flex min-w-0 items-start gap-3">
          <span className="grid size-[34px] shrink-0 place-items-center rounded-full bg-primary-fixed text-primary dark:bg-primary/20 dark:text-primary-fixed-dim">
            <Icon name="edit" className="text-[18px]" />
          </span>
          <div className="min-w-0">
            <h3 className="font-body-md text-[15.5px] font-bold text-on-background dark:text-inverse-on-surface">Angebot in Änderung</h3>
            <p className="mt-0.5 max-w-[60ch] font-body-sm text-[13px] text-secondary dark:text-secondary-fixed-dim" data-testid="aenderung-kue-neu-anfordern">
              Bis {kunde} die neue Kostenübernahme unterschreibt, gilt die bisherige (KÜ {versionsName(gueltige)} vom {formatDate(gueltige.signedAt)}).
            </p>
          </div>
        </div>
        <AenderungVerwerfenKnopf eventId={eventId} versionen={versionen} onVerworfen={onVerworfen} variante="knopf" />
      </div>

      <div className="grid grid-cols-2 border-t border-outline-variant sm:grid-cols-4 dark:border-white/10" data-testid="kue-differenz">
        <Kennzahl testId="kue-diff-alt" label="Gilt jetzt" betrag={wert(gueltigCents)} zusatz={nummer} />
        <Kennzahl
          testId="kue-diff-neu"
          label="Geändertes Angebot"
          betrag={wert(diff?.neu?.cents)}
          zusatz={gesendetAm ? `gesendet am ${formatDate(gesendetAm)}` : "noch nicht gesendet"}
        />
        <Kennzahl testId="kue-diff-differenz" label="Differenz" betrag={diff?.differenzCents != null ? vorzeichen(diff.differenzCents) : "–"} zusatz="" />
        <Kennzahl
          testId="kue-diff-bezahlt"
          label="Bezahlt"
          betrag={wert(diff?.bezahltCents)}
          zusatz={
            diff && diff.ueberzahlungCents > 0
              ? `Überzahlung ${money(diff.ueberzahlungCents)}, wird verrechnet`
              : diff?.offenCents != null && diff.offenCents > 0
                ? `offen nach Unterschrift ${money(diff.offenCents)}`
                : ""
          }
        />
      </div>

      <ol
        className="flex flex-wrap gap-x-6 gap-y-2 border-t border-outline-variant bg-surface-container-low px-5 py-3 font-body-sm text-[13px] dark:border-white/10 dark:bg-white/[0.04]"
        aria-label="Ablauf"
        data-testid="aenderung-schritte"
      >
        {schritte.map((text, i) => {
          const nr = i + 1;
          const stand = nr === 1 || nr < schritt ? "done" : nr === schritt ? "now" : "todo";
          return (
            <li
              key={text}
              data-stand={stand}
              aria-current={stand === "now" ? "step" : undefined}
              className={`flex items-center gap-2 ${stand === "todo" ? "text-secondary dark:text-secondary-fixed-dim" : "text-on-background dark:text-inverse-on-surface"} ${stand === "now" ? "font-semibold" : ""}`}
            >
              <i
                className={`grid size-[22px] shrink-0 place-items-center rounded-full border-[1.5px] text-[11.5px] font-bold not-italic ${
                  stand === "done"
                    ? "border-tertiary bg-tertiary-container text-tertiary dark:border-tertiary-fixed-dim dark:bg-tertiary-fixed-dim/15 dark:text-tertiary-fixed-dim"
                    : stand === "now"
                      ? "border-primary text-primary dark:border-primary-fixed-dim dark:text-primary-fixed-dim"
                      : "border-outline-variant dark:border-white/20"
                }`}
              >
                {stand === "done" ? "✓" : nr}
              </i>
              {text}
            </li>
          );
        })}
      </ol>
    </Card>
  );
}
